/**
 * Authentication Service
 * Handles all authentication-related operations using Strapi Admin API
 */

import { API_CONFIG, getFullAdminUrl } from '../config/api.config';
import { storageService, StoredUser } from '../utils/storage';
import {
  performGetCurrentUser,
  performLogin,
  performRenewToken,
  toStoredUser,
} from './auth-permissions';

// Types
export interface AdminLoginCredentials {
  email: string;
  password: string;
}

export interface AdminAuthResponse {
  data: {
    token: string;
    user: {
      id: number;
      firstname: string;
      lastname: string;
      username: string | null;
      email: string;
      isActive: boolean;
      blocked: boolean;
      preferedLanguage: string | null;
      createdAt: string;
      updatedAt: string;
      roles: Array<{
        id: number;
        name: string;
        code: string;
        description: string;
      }>;
    };
  };
}

export interface AdminUserResponse {
  data: {
    id: number;
    firstname: string;
    lastname: string;
    email: string;
    roles: Array<{
      id: number;
      name: string;
      code: string;
      description: string;
    }>;
  };
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  resetPasswordToken: string;
  password: string;
  passwordConfirmation: string;
}

export interface RenewTokenResponse {
  data: {
    token: string;
  };
}

class AuthService {
  /**
   * Login admin user
   */
  async login(credentials: AdminLoginCredentials): Promise<{ user: StoredUser; token: string }> {
    try {
      const { user, token } = await performLogin(credentials);

      storageService.setToken(token);
      storageService.setUser(user);
      storageService.setPermissions(user.permissions);

      return { user, token };
    } catch (error: any) {
      console.error('❌ [AUTH] Login error:', error);
      console.error('📋 [AUTH] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      throw error;
    }
  }

  /**
   * Get current admin user info
   */
  async getCurrentUser(): Promise<StoredUser> {
    try {
      return await performGetCurrentUser();
    } catch (error: any) {
      console.error('❌ [AUTH] Get current user error:', error);
      const isUnauthorized = error?.status === 401 || error?.error?.status === 401;
      if (isUnauthorized) {
        throw error;
      }
      const storedUser = storageService.getUser();
      if (storedUser) {
        console.log('⚠️ [AUTH] Returning stored user as fallback (non-auth error)');
        return storedUser;
      }
      throw error;
    }
  }

  /**
   * Renew authentication token
   */
  async renewToken(): Promise<string> {
    return performRenewToken();
  }

  /**
   * Forgot password - Send reset email
   */
  async forgotPassword(data: ForgotPasswordData): Promise<void> {
    try {
      const url = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.FORGOT_PASSWORD);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordData): Promise<{ user: StoredUser; token: string }> {
    try {
      const url = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.RESET_PASSWORD);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to reset password');
      }

      const authData: AdminAuthResponse = await response.json();

      const user = toStoredUser(authData.data.user);

      storageService.setToken(authData.data.token);
      storageService.setUser(user);
      storageService.setPermissions(user.permissions);

      return { user, token: authData.data.token };
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const token = storageService.getToken();
      if (token) {
        await fetch(`${API_CONFIG.BASE_URL}/admin/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }).catch(() => {});
      }
    } catch {
      // Best-effort server logout
    } finally {
      storageService.clearAuth();
      window.location.href = '/login';
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return storageService.isAuthenticated();
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return storageService.getToken();
  }

  /**
   * Get stored user
   */
  getUser(): StoredUser | null {
    return storageService.getUser();
  }

  /**
   * Check if user has permission
   */
  hasPermission(permission: string): boolean {
    return storageService.hasPermission(permission);
  }

  /**
   * Check if user has role
   */
  hasRole(roleName: string): boolean {
    return storageService.hasRole(roleName);
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roleNames: string[]): boolean {
    return storageService.hasAnyRole(roleNames);
  }

  /**
   * Refresh user session
   */
  async refreshSession(): Promise<StoredUser> {
    try {
      return await this.getCurrentUser();
    } catch (error) {
      await this.logout();
      throw error;
    }
  }

  /**
   * Validate token and refresh if needed
   */
  async validateSession(): Promise<boolean> {
    try {
      if (!this.isAuthenticated()) {
        return false;
      }

      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService();

export default authService;
